import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList
} from 'recharts';
import api from '../services/api';
import './BaasDashboard.css';

/**
 * BaasDashboard.js — MODELO BANKING (Stark Bank) — refino macro
 * ---------------------------------------------------------------------------
 * Consome só o que o Banking expõe: /account, /balance, /statement, /invoices,
 * /cards, /holders. Sem ledger de compras por cartão, sem /api/baas/dashboard.
 *
 * Seletor de período (7/30/90d) dirige KPIs e gráfico; KPIs de entradas/saídas
 * com delta vs. período anterior; movimentações com saldo corrente; tudo
 * derivado do extrato real.
 *
 * ===========================================================================
 * 🔴 AMBIENTE + POLLING — a combinação que exigia correção
 * ===========================================================================
 * Esta tela recarrega sozinha a cada 30s. A versão anterior NÃO revalidava
 * /account nesse ciclo: se o ambiente fosse trocado em ContaStarkBank (ou em
 * outra aba), o dashboard seguia indefinidamente pintando saldo, entradas e
 * saídas do ambiente antigo — sem nunca perceber.
 *
 * Dashboard é onde se toma decisão. Número de sandbox exibido como produção é
 * pior que tela vazia. Então:
 *
 *  1. TODO ciclo de polling revalida /account. Se o escopo
 *     (ambiente::projectId) mudou, o estado inteiro é descartado e recarregado.
 *  2. Nada renderiza antes do primeiro /account confirmar o ambiente.
 *  3. Selo PRODUÇÃO/SANDBOX no cabeçalho, e faixa de aviso em sandbox.
 *
 * ===========================================================================
 * 🐛 TRÊS BUGS DE DADO CORRIGIDOS AQUI
 * ===========================================================================
 * 1) BUCKET DE DIA EM UTC (`dayKey`). Era `toISOString().slice(0,10)`, que
 *    converte para UTC. No Brasil (UTC-3), toda transação a partir das 21h
 *    local caía no bucket do DIA SEGUINTE — enquanto o rótulo do eixo era
 *    local. O gráfico mentia sobre quando o dinheiro entrou. Agora o bucket
 *    usa componentes locais (`dayKeyLocal`).
 *
 * 2) EXTRATO TRUNCADO EM 100 SEM AVISO. A janela é de 2× o período (até 180
 *    dias), mas só 100 transações eram lidas. Em conta movimentada, "Entradas"
 *    e "Saídas" apareciam MENORES que a realidade, sem nenhuma indicação.
 *    Agora pagina por cursor até um teto e, se ainda houver mais, a tela diz
 *    que os totais são parciais.
 *
 * 3) setState EM COMPONENTE DESMONTADO. Com polling de 30s e nenhum
 *    AbortController, sair da tela deixava requisições em vôo escrevendo em
 *    estado morto. Agora tudo tem `signal` e flag de vida.
 *
 * ===========================================================================
 * ✨ REFINO DE UI (esta revisão)
 * ===========================================================================
 * 1) GRÁFICO "CARTÕES POR LIMITE" reescrito. Nomes longos de titular
 *    encavalavam (o YAxis quebrava "Stephanie Nogueira Machado Barbosa" em 3-4
 *    linhas e elas colidiam entre barras). Agora: nome abreviado a
 *    "primeiro + último" em UMA linha, tick customizado, altura proporcional
 *    ao nº de cartões, rótulo de valor na ponta e tooltip com o nome inteiro.
 *
 * 2) "EXPOSIÇÃO DE LIMITE" (nova seção). Mostra quanto cada cartão representa
 *    do limite total autorizado da carteira — uma leitura de concentração de
 *    risco que o Banking permite calcular sem inventar gasto que ele não expõe.
 *    Verde = equilibrado, âmbar = elevado (≥20%), vermelho pulsante = cartão
 *    concentrado (≥40%). É aqui que as classes .alert-critical / .alert-chip-bar
 *    ganham uso real.
 *
 * 3) MOVIMENTAÇÕES RECENTES ganham um selo (.recent-status-pill) de
 *    "Recarga"/"Despesa", pareado ao ponto colorido da linha.
 * ---------------------------------------------------------------------------
 */

const fmt = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

const fmtShort = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000)     return `R$ ${(n / 1_000).toFixed(1)}K`;
    return fmt(n);
};

/**
 * Chave de dia em horário LOCAL.
 *
 * ⚠️ NÃO troque por toISOString(): aquilo converte para UTC e, em fuso
 * negativo, empurra a transação da noite para o dia seguinte. O eixo do
 * gráfico é rotulado em horário local, então o bucket precisa ser local também.
 */
const dayKeyLocal = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

const dayLabel = (d) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

/**
 * Abrevia nome de titular para caber em UMA linha no eixo do gráfico e nos
 * chips de exposição. "Stephanie Nogueira Machado Barbosa" -> "Stephanie
 * Barbosa"; "Samuel Santos Machado" -> "Samuel Machado". Um só nome fica
 * intacto. É a correção central do encavalamento dos rótulos.
 */
const nomeCurto = (full) => {
    const partes = String(full ?? '').trim().split(/\s+/).filter(Boolean);
    if (partes.length === 0) return '—';
    if (partes.length <= 2) return partes.join(' ');
    const nome = `${partes[0]} ${partes[partes.length - 1]}`;
    return nome.length > 22 ? `${nome.slice(0, 21)}…` : nome;
};

const INTERVAL_LABEL = { day: 'diário', week: 'semanal', month: 'mensal', instant: 'por compra', year: 'anual' };

const PERIODS = [
    { id: 7, label: '7 dias' },
    { id: 30, label: '30 dias' },
    { id: 90, label: '90 dias' },
];

const CHART = { grid: 'rgba(15,23,42,.06)', tick: '#64748b' };

const DAY_MS = 86_400_000;
const POLL_MS = 30_000;
const STMT_PAGE = 100;        // por requisição (teto da API)
const STMT_MAX_PAGES = 12;    // teto de páginas: 1.200 transações por carga
const REFRESH_MIN_MS = 3_000; // anti-marteladas no botão de atualizar

// Faixas de concentração de limite (participação no total autorizado).
const EXPO_CRIT = 0.40;   // um cartão sozinho concentra 40%+ do limite total
const EXPO_WARN = 0.20;   // participação elevada
const EXPO_TOP  = 8;      // no máximo 8 cartões na lista de exposição

const ENV_LABEL = { production: 'PRODUÇÃO', sandbox: 'SANDBOX' };
const envLabelOf = (env) => ENV_LABEL[env] || String(env || '').toUpperCase() || '—';

const asArray = (v) => (Array.isArray(v) ? v : []);
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const apiErr = (e) => {
    if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') return null;
    const status = e?.response?.status;
    if (status === 403) return 'Seu perfil não tem permissão para ver o dashboard financeiro.';
    if (status === 409) return 'A conta Stark Bank não está conectada.';
    return e?.response?.data?.error || 'Falha ao carregar o dashboard. Verifique a conexão.';
};

const scopeOf = (account) => (account
    ? `${account.environment || '?'}::${account.stark_project_id || '?'}`
    : null);

function deltaOf(cur, prev, goodWhenUp) {
    if (!prev || prev <= 0) return null;
    const pct = ((cur - prev) / prev) * 100;
    if (!Number.isFinite(pct) || Math.abs(pct) < 0.5) return null;
    const up = cur >= prev;
    return { pct, up, good: goodWhenUp ? up : !up };
}

// ── Animated counter ────────────────────────────────────────────────────────
function AnimatedNumber({ value, decimals = 0 }) {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        const target = num(value);
        // Alvo zero (ou negativo) não precisa de animação — e evita divisão
        // que nunca converge.
        if (target <= 0) { setDisplay(target); return undefined; }
        let start = 0;
        const step = target / 30;
        const timer = setInterval(() => {
            start += step;
            if (start >= target) { setDisplay(target); clearInterval(timer); }
            else setDisplay(start);
        }, 16);
        return () => clearInterval(timer);
    }, [value]);
    return <span>{display.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</span>;
}

function KpiCard({ label, value, sub, accent, icon, action, delta }) {
    return (
        <div className="baas-kpi" style={{ '--accent': accent }}>
            <div className="baas-kpi-top">
                <span className="baas-kpi-icon">{icon}</span>
                {delta && (
                    <span className={`baas-kpi-delta ${delta.good ? 'delta-up' : 'delta-down'}`}>
                        {delta.up ? '▲' : '▼'} {Math.abs(delta.pct).toFixed(0)}%
                    </span>
                )}
            </div>
            <div className="baas-kpi-value">
                {typeof value === 'string' ? value : <AnimatedNumber value={value} />}
            </div>
            <div className="baas-kpi-label">{label}</div>
            {sub && <div className="baas-kpi-sub">{sub}</div>}
            {action && <div className="baas-kpi-action">{action}</div>}
            <div className="baas-kpi-bar" />
        </div>
    );
}

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="baas-tooltip">
            <p className="tooltip-label">{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
            ))}
        </div>
    );
};

/** Tooltip do gráfico de limites: nome COMPLETO + limite + intervalo + tipo. */
const LimitTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload || {};
    const fisico = p.type === 'physical';
    return (
        <div className="baas-tooltip">
            <p className="tooltip-label">{p.nomeCompleto || p.nome}</p>
            <p style={{ color: fisico ? '#7c3aed' : '#3b82f6' }}>
                Limite: {fmt(p.limite)} ({p.intervalo})
            </p>
            <p style={{ color: '#64748b' }}>{fisico ? 'Cartão físico' : 'Cartão virtual'}</p>
        </div>
    );
};

/**
 * Tick do eixo Y do gráfico de limites. Renderiza o nome já abreviado em UMA
 * linha — é o que elimina o encavalamento (o tick padrão do recharts quebrava
 * nomes longos em várias linhas que colidiam entre barras vizinhas).
 */
function LimitYAxisTick({ x, y, payload }) {
    return (
        <text x={x} y={y} dy={4} textAnchor="end" className="baas-limit-tick">
            {payload?.value}
        </text>
    );
}

/** Selo de ambiente. */
function EnvBadge({ environment }) {
    if (!environment) return null;
    const prod = environment === 'production';
    return (
        <span className={`baas-env-badge ${prod ? 'is-prod' : 'is-sbx'}`}>
            ● {envLabelOf(environment)}
        </span>
    );
}

export default function BaasDashboard() {
    const [hydrated, setHydrated] = useState(false);
    const [connected, setConnected] = useState(false);
    const [account, setAccount] = useState(null);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [period, setPeriod] = useState(30);

    const [balance, setBalance] = useState(null);
    const [statement, setStatement] = useState([]);
    const [stmtTruncado, setStmtTruncado] = useState(false);
    const [cards, setCards] = useState([]);
    const [holders, setHolders] = useState([]);
    const [invoices, setInvoices] = useState([]);

    const aliveRef = useRef(true);
    const inFlightRef = useRef(null);
    const lastRefreshRef = useRef(0);
    const scopeRef = useRef(null);

    useEffect(() => () => {
        aliveRef.current = false;
        inFlightRef.current?.abort();
    }, []);

    const environment = account?.environment || null;
    const isProd = environment === 'production';

    /** Zera tudo que pertence a um escopo — usado quando o ambiente troca. */
    const limparEscopo = useCallback(() => {
        setBalance(null);
        setStatement([]);
        setStmtTruncado(false);
        setCards([]);
        setHolders([]);
        setInvoices([]);
    }, []);

    /**
     * Lê o extrato paginando por cursor.
     *
     * ⚠️ Uma única página de 100 fazia os KPIs de entradas/saídas mostrarem
     * MENOS dinheiro do que realmente circulou, silenciosamente. Aqui a
     * paginação vai até STMT_MAX_PAGES e devolve `truncado` quando ainda sobra
     * — a tela precisa poder dizer que o total é parcial.
     */
    const fetchStatement = useCallback(async (days, signal) => {
        const after = new Date(Date.now() - 2 * days * DAY_MS).toISOString().slice(0, 10);
        const acc = [];
        let cursor = null;
        let paginas = 0;

        do {
            const params = { limit: STMT_PAGE, after, ...(cursor ? { cursor } : {}) };
            const { data } = await api.get('/api/baas/account/statement', { params, signal });
            const lote = asArray(data?.transactions);
            acc.push(...lote);
            cursor = data?.cursor || null;
            paginas += 1;
            if (lote.length === 0) break;
        } while (cursor && paginas < STMT_MAX_PAGES);

        return { transactions: acc, truncado: !!cursor };
    }, []);

    const loadData = useCallback(async (days, signal) => {
        const [bal, stmt, crd, hld, inv] = await Promise.allSettled([
            api.get('/api/baas/account/balance', { signal }),
            fetchStatement(days, signal),
            api.get('/api/baas/account/cards', { signal }),
            api.get('/api/baas/account/holders', { signal }),
            api.get('/api/baas/account/invoices', { signal }),
        ]);

        if (!aliveRef.current || signal?.aborted) return;

        if (bal.status === 'fulfilled') setBalance(bal.value.data);
        if (stmt.status === 'fulfilled') {
            setStatement(asArray(stmt.value.transactions));
            setStmtTruncado(!!stmt.value.truncado);
        }
        if (crd.status === 'fulfilled') setCards(asArray(crd.value.data));
        if (hld.status === 'fulfilled') setHolders(asArray(hld.value.data));
        if (inv.status === 'fulfilled') setInvoices(asArray(inv.value.data));
    }, [fetchStatement]);

    /**
     * Carga completa: SEMPRE começa confirmando a conta e o ambiente.
     *
     * `silent = true` é o ciclo de polling — não acende spinner, mas revalida o
     * ambiente do mesmo jeito. É exatamente essa revalidação que faltava.
     */
    const load = useCallback(async (days, { silent = false } = {}) => {
        inFlightRef.current?.abort();
        const controller = new AbortController();
        inFlightRef.current = controller;

        try {
            const { data } = await api.get('/api/baas/account', { signal: controller.signal });
            if (!aliveRef.current) return;

            const nextAccount = data?.account || null;
            const isConnected = !!data?.connected;
            const nextScope = scopeOf(nextAccount);

            // 🔴 Ambiente trocou enquanto a tela estava aberta: o que está em
            // memória é de outro ambiente e não pode continuar em tela.
            if (scopeRef.current && scopeRef.current !== nextScope) {
                limparEscopo();
            }
            scopeRef.current = nextScope;

            setAccount(nextAccount);
            setConnected(isConnected);
            setError(null);

            if (isConnected) await loadData(days, controller.signal);
            else limparEscopo();
        } catch (e) {
            const msg = apiErr(e);
            if (msg && aliveRef.current) setError(msg);
        } finally {
            if (inFlightRef.current === controller) inFlightRef.current = null;
            if (aliveRef.current && !controller.signal.aborted) {
                setHydrated(true);
                if (!silent) setRefreshing(false);
            }
        }
    }, [loadData, limparEscopo]);

    useEffect(() => { load(period); }, [load, period]);

    // Polling: revalida a CONTA junto, não só os dados.
    useEffect(() => {
        if (!connected) return undefined;
        const t = setInterval(() => { load(period, { silent: true }); }, POLL_MS);
        return () => clearInterval(t);
    }, [connected, load, period]);

    const handleRefresh = useCallback(async () => {
        // Cada refresh dispara 5+ chamadas à Stark. Sem throttle, marteladas no
        // botão viram rajada de requisições e rate limit.
        const agora = Date.now();
        if (agora - lastRefreshRef.current < REFRESH_MIN_MS) return;
        lastRefreshRef.current = agora;
        setRefreshing(true);
        await load(period);
    }, [load, period]);

    if (!hydrated) {
        return (
            <div className="baas-dash-loading">
                <div className="baas-spinner" />
                <p>Confirmando ambiente e consolidando dados da conta...</p>
            </div>
        );
    }
    if (error) {
        return (
            <div className="baas-dash-error">
                <span>⚠️</span><p>{error}</p>
                <button onClick={() => load(period)}>Tentar novamente</button>
            </div>
        );
    }
    if (!connected) {
        return (
            <div className="baas-dash-error">
                <span>🔌</span>
                <p>Nenhuma conta Stark Bank conectada. Conecte a conta na tela "Conta Digital Stark Bank" para ver o dashboard.</p>
            </div>
        );
    }

    return (
        <DashboardBody
            account={account}
            environment={environment}
            isProd={isProd}
            period={period}
            setPeriod={setPeriod}
            refreshing={refreshing}
            onRefresh={handleRefresh}
            balance={balance}
            statement={statement}
            stmtTruncado={stmtTruncado}
            cards={cards}
            holders={holders}
            invoices={invoices}
        />
    );
}

// =============================================================================
// CORPO DO DASHBOARD — separado para que os cálculos só rodem quando há dados
// confirmados do ambiente certo.
// =============================================================================
function DashboardBody({
    environment, isProd, period, setPeriod, refreshing, onRefresh,
    balance, statement, stmtTruncado, cards, holders, invoices,
}) {
    const periodLabel = useMemo(
        () => PERIODS.find((p) => p.id === period)?.label || `${period} dias`,
        [period],
    );

    // ── Janelas de tempo ────────────────────────────────────────────────────
    // `now` fica memoizado por período/dados para não mudar a cada render e
    // provocar recálculo em cascata.
    const { curTx, prevTx } = useMemo(() => {
        const agora = Date.now();
        const curStart = agora - period * DAY_MS;
        const prevStart = agora - 2 * period * DAY_MS;

        const cur = [];
        const prev = [];
        for (const t of asArray(statement)) {
            const ts = new Date(t?.created).getTime();
            if (Number.isNaN(ts)) continue;      // transação sem data válida não entra em conta
            if (ts >= curStart) cur.push(t);
            else if (ts >= prevStart) prev.push(t);
        }
        return { curTx: cur, prevTx: prev };
    }, [statement, period]);

    const sumIn = (arr) => arr.reduce((s, t) => (num(t.amount) > 0 ? s + num(t.amount) : s), 0);
    const sumOut = (arr) => arr.reduce((s, t) => (num(t.amount) < 0 ? s + Math.abs(num(t.amount)) : s), 0);

    const entradas = useMemo(() => sumIn(curTx), [curTx]);
    const saidas = useMemo(() => sumOut(curTx), [curTx]);
    const deltaEntradas = useMemo(() => deltaOf(entradas, sumIn(prevTx), true), [entradas, prevTx]);
    const deltaSaidas = useMemo(() => deltaOf(saidas, sumOut(prevTx), false), [saidas, prevTx]);

    const nCreditos = useMemo(() => curTx.filter((t) => num(t.amount) > 0).length, [curTx]);
    const nDebitos = useMemo(() => curTx.filter((t) => num(t.amount) < 0).length, [curTx]);

    const cartoesAtivos = useMemo(() => asArray(cards).filter((c) => c?.status === 'active').length, [cards]);
    const cartoesInativos = Math.max(0, asArray(cards).length - cartoesAtivos);
    const fisicosPendentes = useMemo(
        () => asArray(cards).filter((c) => c?.type === 'physical' && c?.status === 'pending'),
        [cards],
    );
    const externos = useMemo(
        () => asArray(cards).filter((c) => c?.origem === 'stark').length,
        [cards],
    );

    const { aportesPagos, nAportesPagos } = useMemo(() => {
        const agora = Date.now();
        const curStart = agora - period * DAY_MS;
        let total = 0; let n = 0;
        for (const i of asArray(invoices)) {
            if (i?.status !== 'paid') continue;
            n += 1;
            const ts = new Date(i?.created_at).getTime();
            if (!Number.isNaN(ts) && ts >= curStart) total += num(i.amount);
        }
        return { aportesPagos: total, nAportesPagos: n };
    }, [invoices, period]);

    // ── Série do gráfico: buckets diários em horário LOCAL ──────────────────
    // ⚠️ A chave do bucket e o rótulo do eixo precisam vir do MESMO fuso.
    // Antes o bucket era UTC e o rótulo local: transação das 22h aparecia no
    // dia seguinte.
    const trend = useMemo(() => {
        const agora = Date.now();
        const buckets = new Map();
        for (let i = period - 1; i >= 0; i--) {
            const d = new Date(agora - i * DAY_MS);
            buckets.set(dayKeyLocal(d.toISOString()), { dia: dayLabel(d), entradas: 0, saidas: 0 });
        }
        for (const t of curTx) {
            const b = buckets.get(dayKeyLocal(t?.created));
            if (!b) continue;
            const v = num(t.amount);
            if (v >= 0) b.entradas += v; else b.saidas += Math.abs(v);
        }
        return [...buckets.values()];
    }, [curTx, period]);

    // ── Top cartões por limite (gráfico) ────────────────────────────────────
    // Guarda o nome COMPLETO para o tooltip e o abreviado para o eixo.
    const topCartoes = useMemo(() => asArray(cards)
        .map((c) => {
            const rule = (Array.isArray(c?.rules) && c.rules[0]) || null;
            const full = c?.holder_name || c?.display_name || '—';
            return {
                nome: nomeCurto(full),
                nomeCompleto: full,
                limite: rule ? num(rule.amount) : 0,
                intervalo: rule ? (INTERVAL_LABEL[rule.interval] || rule.interval || '—') : '—',
                type: c?.type,
            };
        })
        .filter((c) => c.limite > 0)
        .sort((a, b) => b.limite - a.limite)
        .slice(0, 6), [cards]);

    // Altura proporcional ao nº de barras: 6 cartões deixam de se espremer.
    const limitChartHeight = Math.max(240, topCartoes.length * 48 + 44);

    // ── Exposição de limite: participação de cada cartão no total autorizado ─
    // Concentração é a leitura de risco que o modelo Banking permite fazer sem
    // inventar gasto (que ele não expõe por cartão). Só cartões ATIVOS com
    // limite entram — cartão bloqueado/cancelado não gera exposição.
    const exposicao = useMemo(() => {
        const comLimite = asArray(cards)
            .filter((c) => c?.status === 'active')
            .map((c) => {
                const rule = (Array.isArray(c?.rules) && c.rules[0]) || null;
                return {
                    nome: nomeCurto(c?.holder_name || c?.display_name || '—'),
                    limite: rule ? num(rule.amount) : 0,
                    intervalo: rule ? (INTERVAL_LABEL[rule.interval] || rule.interval || '—') : '—',
                };
            })
            .filter((c) => c.limite > 0);

        const total = comLimite.reduce((s, c) => s + c.limite, 0);
        if (total <= 0) return { total: 0, itens: [], concentrados: 0 };

        const itens = comLimite
            .map((c) => ({ ...c, share: c.limite / total }))
            .sort((a, b) => b.share - a.share)
            .slice(0, EXPO_TOP);

        const concentrados = itens.filter((c) => c.share >= EXPO_CRIT).length;
        return { total, itens, concentrados };
    }, [cards]);

    const recent = useMemo(() => curTx.slice(0, 8), [curTx]);
    const pctAtivos = asArray(cards).length > 0 ? (cartoesAtivos / asArray(cards).length) * 100 : 0;

    return (
        <div className="baas-dash">
            {/* ── Cabeçalho + seletor de período ──────────────────────────── */}
            <div className="baas-dash-head">
                <div>
                    <h2>
                        Visão geral da conta
                        <EnvBadge environment={environment} />
                    </h2>
                    <p>Saldo, fluxo e cartões da conta Stark Bank conectada · {envLabelOf(environment)}.</p>
                </div>
                <div className="baas-head-controls">
                    <div className="baas-period" role="tablist">
                        {PERIODS.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                className={`baas-period-btn ${period === p.id ? 'active' : ''}`}
                                onClick={() => setPeriod(p.id)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <button className="baas-head-refresh" onClick={onRefresh} disabled={refreshing} title="Atualizar">
                        <span className={refreshing ? 'spinning' : ''}>⟳</span>
                    </button>
                </div>
            </div>

            {/* Números de sandbox num painel de decisão precisam de rótulo. */}
            {!isProd && (
                <div className="baas-env-alert">
                    <strong>Dados do ambiente de TESTES (Sandbox).</strong>
                    <span> Saldo, entradas, saídas e aportes abaixo não refletem dinheiro real. Não use estes números para decisão nem para relatório.</span>
                </div>
            )}

            {/* Totais parciais têm que se declarar parciais. */}
            {stmtTruncado && (
                <div className="baas-trunc-alert">
                    <strong>Totais parciais.</strong>
                    <span> A conta tem mais movimentações no período do que foi possível ler numa consulta. Entradas, saídas e o gráfico consideram apenas as mais recentes — reduza o período para números exatos.</span>
                </div>
            )}

            {/* ── KPIs ────────────────────────────────────────────────────── */}
            <section className="baas-kpi-row">
                <KpiCard
                    label="Saldo disponível"
                    value={balance ? fmt(balance.amount) : '— —'}
                    sub={balance?.updated
                        ? `Atualizado ${new Date(balance.updated).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                        : 'Sincronizando...'}
                    accent="#7c3aed" icon="🏦"
                    action={
                        <button className="baas-kpi-sync-btn" onClick={onRefresh} disabled={refreshing}>
                            {refreshing ? 'Atualizando...' : '⟳ Atualizar saldo'}
                        </button>
                    }
                />
                <KpiCard
                    label={`Entradas · ${periodLabel}`}
                    value={fmt(entradas)}
                    sub={`${nCreditos} crédito${nCreditos === 1 ? '' : 's'}${stmtTruncado ? ' (parcial)' : ''}`}
                    accent="#10b981" icon="⬆️" delta={deltaEntradas}
                />
                <KpiCard
                    label={`Saídas · ${periodLabel}`}
                    value={fmt(saidas)}
                    sub={`${nDebitos} débito${nDebitos === 1 ? '' : 's'}${stmtTruncado ? ' (parcial)' : ''}`}
                    accent="#ef4444" icon="⬇️" delta={deltaSaidas}
                />
                <KpiCard
                    label="Cartões"
                    value={asArray(cards).length}
                    sub={`${cartoesAtivos} ativos · ${cartoesInativos} inativos${externos > 0 ? ` · ${externos} externo${externos === 1 ? '' : 's'}` : ''}`}
                    accent="#3b82f6" icon="💳"
                />
                <KpiCard label="Titulares" value={asArray(holders).length} sub="CorporateHolders na conta" accent="#8b5cf6" icon="👤" />
                <KpiCard
                    label={`Aportes pagos · ${periodLabel}`}
                    value={fmt(aportesPagos)}
                    sub={`${nAportesPagos} cobrança${nAportesPagos === 1 ? '' : 's'} paga${nAportesPagos === 1 ? '' : 's'}`}
                    accent="#f59e0b" icon="🧾"
                />
            </section>

            {/* ── Alerta: cartões físicos aguardando desbloqueio ──────────── */}
            {fisicosPendentes.length > 0 && (
                <section className="baas-alerts-row">
                    <div className="baas-alerts-title">
                        <span className="alert-pulse" /> Cartões físicos aguardando desbloqueio
                    </div>
                    <div className="baas-alerts-list">
                        {fisicosPendentes.map((c, i) => (
                            <div key={c.stark_card_id || `pend-${i}`} className="baas-alert-chip alert-warning">
                                <span className="alert-chip-name">{c.holder_name || c.display_name || '—'}</span>
                                <span className="alert-chip-pct">•••• {c.last4 || '????'}</span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* ── GRID PRINCIPAL ──────────────────────────────────────────── */}
            <div className="baas-dash-grid">
                <div className="baas-chart-card baas-chart-wide">
                    <div className="chart-header">
                        <h3>Movimentação · {periodLabel}</h3>
                        <div className="chart-legend">
                            <span><span className="legend-dot" style={{ background: '#10b981' }} />Entradas</span>
                            <span><span className="legend-dot" style={{ background: '#ef4444' }} />Saídas</span>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={230}>
                        <AreaChart data={trend} margin={{ top: 5, right: 18, bottom: 5, left: 8 }}>
                            <defs>
                                <linearGradient id="gEntrada" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.28} />
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gSaida" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.25} />
                                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
                            <XAxis dataKey="dia" tick={{ fontSize: 11, fill: CHART.tick }} axisLine={false} tickLine={false} minTickGap={24} />
                            <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: CHART.tick }} axisLine={false} tickLine={false} width={64} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="entradas" name="Entradas" stroke="#10b981" fill="url(#gEntrada)" strokeWidth={2} />
                            <Area type="monotone" dataKey="saidas" name="Saídas" stroke="#ef4444" fill="url(#gSaida)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                <div className="baas-recent-card">
                    <div className="chart-header">
                        <h3>Movimentações recentes</h3>
                        <span className="chart-sub">{recent.length} de {curTx.length}{stmtTruncado ? '+' : ''}</span>
                    </div>
                    <div className="baas-recent-list">
                        {recent.length === 0 && <div className="baas-empty">Nenhuma movimentação no período.</div>}
                        {recent.map((t, i) => {
                            const entrada = num(t.amount) >= 0;
                            return (
                                <div key={t.id || `tx-${i}`} className="baas-recent-row">
                                    <div className={`recent-type-dot ${entrada ? 'recarga' : 'despesa'}`} />
                                    <div className="recent-info">
                                        <span className="recent-desc">
                                            {t.description || 'Movimentação'}
                                            <i className="recent-stark-tag">Stark</i>
                                        </span>
                                        <span className="recent-card">{t.source || 'Conta Digital'}</span>
                                    </div>
                                    <span className={`recent-status-pill ${entrada ? 'pill-recarga' : 'pill-despesa'}`}>
                                        {entrada ? 'Recarga' : 'Despesa'}
                                    </span>
                                    <span className="recent-saldo">{fmt(t.balance)}</span>
                                    <div className={`recent-valor ${entrada ? 'val-green' : 'val-red'}`}>
                                        {entrada ? '+' : '−'} {fmt(Math.abs(num(t.amount)))}
                                    </div>
                                    <div className="recent-date">
                                        {t.created
                                            ? new Date(t.created).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                                            : '—'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="baas-gauge-card">
                    <div className="chart-header"><h3>Saúde da carteira</h3></div>
                    <div className="gauge-wrapper">
                        <svg viewBox="0 0 200 120" className="gauge-svg">
                            <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="#e2e8f0" strokeWidth="18" strokeLinecap="round" />
                            <path
                                d="M20 100 A80 80 0 0 1 180 100"
                                fill="none"
                                stroke={pctAtivos > 66 ? '#10b981' : pctAtivos > 33 ? '#f59e0b' : '#ef4444'}
                                strokeWidth="18" strokeLinecap="round"
                                strokeDasharray={`${pctAtivos * 2.51} 251`}
                                style={{ transition: 'stroke-dasharray 1s ease' }}
                            />
                            <text x="100" y="95" textAnchor="middle" className="gauge-pct">{pctAtivos.toFixed(0)}%</text>
                            <text x="100" y="112" textAnchor="middle" className="gauge-sub-text">cartões ativos</text>
                        </svg>
                        <div className="gauge-details">
                            <div className="gauge-row"><span>Total de cartões</span><strong>{asArray(cards).length}</strong></div>
                            <div className="gauge-row"><span>Ativos</span><strong className="txt-green">{cartoesAtivos}</strong></div>
                            <div className="gauge-row"><span>Físicos pendentes</span><strong>{fisicosPendentes.length}</strong></div>
                            <div className="gauge-row"><span>Titulares</span><strong>{asArray(holders).length}</strong></div>
                            {externos > 0 && (
                                <div className="gauge-row"><span>Emitidos fora da plataforma</span><strong>{externos}</strong></div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="baas-chart-card baas-chart-limits">
                    <div className="chart-header">
                        <h3>Cartões por limite</h3>
                        <div className="chart-legend">
                            <span><span className="legend-dot" style={{ background: '#7c3aed' }} />Físico</span>
                            <span><span className="legend-dot" style={{ background: '#3b82f6' }} />Virtual</span>
                        </div>
                    </div>
                    {topCartoes.length === 0 ? (
                        <div className="baas-empty">Nenhum cartão com limite definido.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={limitChartHeight}>
                            <BarChart data={topCartoes} layout="vertical" barCategoryGap="30%" margin={{ top: 6, right: 56, left: 8, bottom: 6 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} horizontal={false} />
                                <XAxis type="number" tickFormatter={fmtShort} tick={{ fontSize: 11, fill: CHART.tick }} axisLine={false} tickLine={false} />
                                <YAxis type="category" dataKey="nome" width={140} interval={0} tick={<LimitYAxisTick />} axisLine={false} tickLine={false} />
                                <Tooltip content={<LimitTooltip />} cursor={{ fill: 'rgba(124,58,237,0.06)' }} />
                                <Bar dataKey="limite" name="Limite" radius={[0, 6, 6, 0]} barSize={22}>
                                    {topCartoes.map((c, i) => (
                                        <Cell key={i} fill={c.type === 'physical' ? '#7c3aed' : '#3b82f6'} />
                                    ))}
                                    <LabelList dataKey="limite" position="right" formatter={fmtShort} fill="#64748b" fontSize={11} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* ── Exposição de limite (concentração de risco) ─────────────
                    Aqui vivem .alert-chip-bar / .alert-critical. Cada barra é a
                    participação do cartão no limite total autorizado da carteira.
                    Só aparece quando há cartão ativo com limite. */}
                {exposicao.itens.length > 0 && (
                    <div className="baas-exposure-card">
                        <div className="chart-header">
                            <h3>Exposição de limite</h3>
                            <span className="chart-sub">
                                participação de cada cartão no limite total autorizado ({fmt(exposicao.total)})
                            </span>
                        </div>
                        <div className="baas-exposure-list">
                            {exposicao.itens.map((c, i) => {
                                const pct = c.share * 100;
                                const nivel = c.share >= EXPO_CRIT ? 'critical'
                                    : c.share >= EXPO_WARN ? 'warning' : 'ok';
                                return (
                                    <div key={`${c.nome}-${i}`} className={`baas-alert-chip alert-${nivel} exposure-chip`}>
                                        <span className="alert-chip-name">{c.nome}</span>
                                        <span className="exposure-chip-limit">{fmt(c.limite)} · {c.intervalo}</span>
                                        <span className="alert-chip-bar"><div style={{ width: `${Math.min(100, pct)}%` }} /></span>
                                        <span className="alert-chip-pct">{pct.toFixed(0)}%</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="baas-exposure-legend">
                            {exposicao.concentrados > 0 ? (
                                <span className="exposure-alarm">
                                    {exposicao.concentrados} cartão(ões) concentram parte relevante do limite — reveja se é intencional.
                                </span>
                            ) : (
                                <span className="exposure-ok">Limite bem distribuído entre os cartões.</span>
                            )}
                            <span className="exposure-scale">
                                <i className="dot-ok" /> equilibrado
                                <i className="dot-warn" /> elevado (≥20%)
                                <i className="dot-crit" /> concentrado (≥40%)
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}